import { useQuery } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { PermissionsGroupsEnumType } from '@/features/auth/types'
import { selectGetTempSelectedGroupId } from '@/features/groups/stores/groups/groups.selectors'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import {
  selectGetSelectedPeriodId,
  selectGetSelectedProgramId,
} from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { getSessionsAdminManager, V1_SESSIONS_ADMIN_MANAGER_QUERY_KEY } from '@/features/sessions/api'
import type { GetSessionsResponseType } from '@/features/sessions/types'
import { SessionStatusEnumType, type V2SessionType } from '@/features/sessions/types'
import { setAdminSessionStatus } from '@/features/sessions/utils'
import checkPermissionsGroup from '@/features/users/utils'
import { MAX_SESSIONS_PER_PAGE } from '@/utils/config/constants'
function V1ToV2DataMiddleware({
  data,
  userId,
}: {
  data: GetSessionsResponseType | undefined
  userId: string | undefined
}): V2SessionType[] {
  const sessions = data?.sessions?.map((session) => {
    const groupUsersMap = new Map(
      session.mentorshipGroup.groupUsers
        // get ONLY the mentor and mentees
        .filter((groupUser) => checkPermissionsGroup(groupUser.roles, PermissionsGroupsEnumType.USER))
        .map((groupUser) => [
          groupUser.user.id,
          {
            roles: groupUser.roles,
            picture: groupUser.user.personalInformation?.profilPicture ?? null,
          },
        ]),
    )

    const usersInSession = session.users
      .filter((user) => groupUsersMap.has(user.id))
      .map((user) => {
        const groupUserData = groupUsersMap.get(user.id)!
        return {
          id: user.id,
          roles: groupUserData.roles,
          firstName: user.identity.firstname,
          lastName: user.identity.lastname,
          picture: user.personalInformation?.profilPicture ?? groupUserData.picture,
          email: user.email,
          sessionStatus:
            session.statuses?.find((status) => status.userId === user.id)?.status || SessionStatusEnumType.NONE,
        }
      })

    let sessionStatus: SessionStatusEnumType
    const isUserInSession = usersInSession.some((user) => user.id === userId)

    // if the user is in the session, we get his status
    if (isUserInSession) {
      sessionStatus = session.statuses.find((status) => status.userId === userId)?.status || SessionStatusEnumType.NONE
      // else if it's admin, we get the status of the session
    } else {
      sessionStatus = setAdminSessionStatus(session)
    }

    return {
      id: session.id,
      mentoringGroupId: session.mentoringGroupId,
      mentoringGroupName: session.mentorshipGroup.customName ?? session.mentorshipGroup.name,
      meetingRoomId: session.mentorshipGroup.meetingRoomId,
      at: session.from,
      from: session.from,
      to: session.to,
      name: session.name,
      location: session.location,
      duration: session.duration,
      status: sessionStatus,
      report: session.report ?? null,
      users: usersInSession,
      createdAt: session.createdAt,
      updatedAt: session.updatedAt,
      reviews: session.reviews,
      presences: session.presences.map((presence) => ({
        userId: presence.userId,
        wasPresent: presence.isPresent,
      })),
      deliverables: session.deliverables,
    } satisfies V2SessionType
  })

  return sessions || []
}

const useSessions = ({
  pageRef,
  perPageRef,
  filterEmailSearchRef,
}: {
  pageRef?: Ref<number>
  perPageRef?: Ref<number>
  filterEmailSearchRef?: Ref<string | null>
} = {}) => {
  const { accountRef } = useAccount()
  const tempSelectedGroupIdRef = selectGetTempSelectedGroupId(groupsStore)
  const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
  const selectedPeriodIdRef = selectGetSelectedPeriodId(programsStore)

  const getSessionsPageRef = computed(() => pageRef?.value || 1)
  const getSessionsPerPageRef = computed(() => perPageRef?.value || 20)
  const getSessionsFilterEmailSearchRef = computed(() => filterEmailSearchRef?.value || '')
  // Check if all the required data is available to make the query
  const isSessionsQueryEnabledRef = computed(
    () =>
      // we check for the program, period, account and group (required to make the query)
      !!selectedProgramIdRef.value && !!selectedPeriodIdRef.value && !!accountRef.value,
  )

  const sessionsQuery = useQuery({
    queryKey: [
      V1_SESSIONS_ADMIN_MANAGER_QUERY_KEY,
      {
        periodId: selectedPeriodIdRef,
        groupId: tempSelectedGroupIdRef,
        perPage: getSessionsPerPageRef,
        page: getSessionsPageRef,
        filterEmail: getSessionsFilterEmailSearchRef,
      },
    ],
    queryFn: () =>
      getSessionsAdminManager({
        programId: selectedProgramIdRef.value!,
        periodId: selectedPeriodIdRef.value!,
        groupId: tempSelectedGroupIdRef.value!,

        // including the page metadata
        page: getSessionsPageRef.value!,
        perPage: getSessionsPerPageRef.value ?? MAX_SESSIONS_PER_PAGE,
        filterEmail: getSessionsFilterEmailSearchRef.value!,
      }),
    enabled: isSessionsQueryEnabledRef,
  })

  const data = computed(() => {
    return V1ToV2DataMiddleware({
      data: sessionsQuery.data.value,
      userId: accountRef.value?.id,
    })
  })

  const totalPagesRef = computed(() => sessionsQuery.data.value?.totalPages)
  const totalSessionsRef = computed(() => sessionsQuery.data.value?.totalSessions)

  return {
    sessionsRef: data,
    totalPagesRef,
    totalSessionsRef,
    isPendingRef: sessionsQuery.isPending,
  }
}

export default useSessions
